import { CatalogFeature, CatalogFeatureId } from '@weavix/models/src/packaging/feature-catalog';
import { accountDefaultProductPackagesId, isAccountDefaultProductPackages } from '@weavix/models/src/packaging/sync-product-packages';
import { ProductPackageEntitlements, resolveSiteEntitlements } from './resolve-product-packages';

/** A record from a client's packages store: a definition, or an account's default (`packages`). */
export interface SyncedProductPackageRecord {
    id: string;
    /** Account whose topic delivered the record. */
    accountId?: string;
    features?: ReadonlyArray<CatalogFeatureId>;
    ownerAccountId?: string;
    packages?: ReadonlyArray<string>;
}

/** A site from a client's facilities store, with the packages assigned to it. */
export interface SyncedSiteProductPackages {
    id: string;
    accountId?: string;
    packages?: ReadonlyArray<string>;
}

/** Why synced packages could not decide a site's entitlements, so the caller uses `enabledActions`. */
export enum SyncedEntitlementFallback {
    NotSynced = 'not-synced',
    NoDefinedPackages = 'no-defined-packages',
}

/** Entitlements resolved from synced records; when null, `fallback` says why and `packageIds` what could not be found. */
export interface SyncedSiteEntitlements {
    entitlements: ProductPackageEntitlements | null;
    fallback?: SyncedEntitlementFallback;
    packageIds?: string[];
}

/**
 * Resolves a site of `accountId` from what a client has synced, as the backend gate does; `facilityId` null counts the
 * whole account. Falls back until the account's default record has synced, and when none of the site's packages is defined.
 */
export function resolveSyncedSiteEntitlements(
    accountId: string,
    facilityId: string | null,
    records: ReadonlyArray<SyncedProductPackageRecord>,
    sites: ReadonlyArray<SyncedSiteProductPackages>,
    catalog: Readonly<Record<string, CatalogFeature>>,
): SyncedSiteEntitlements {
    const defaultId = accountDefaultProductPackagesId(accountId);
    const accountDefault = records.find(record => record.id === defaultId);
    if (!accountDefault) return { entitlements: null, fallback: SyncedEntitlementFallback.NotSynced };

    const definitions = records
        .filter(record => !isAccountDefaultProductPackages(record))
        .map(record => ({
            id: record.id, name: record.id, description: '', features: [...record.features ?? []], requires: [],
            ...(record.ownerAccountId ? { ownerAccountId: record.ownerAccountId } : {}),
        }));
    const sitePackages = Object.fromEntries(sites
        .filter(site => site.accountId === accountId && site.packages?.length)
        .map(site => [site.id, site.packages]));

    const { packageIds, entitlements } = resolveSiteEntitlements(
        { accountId, accountPackages: accountDefault.packages, sitePackages, definitions },
        facilityId,
        catalog,
    );
    return entitlements ? { entitlements } : { entitlements: null, fallback: SyncedEntitlementFallback.NoDefinedPackages, packageIds };
}

/** A synced `SiteScopedPackages` flag record, as far as entitlements care. */
export interface SyncedFlagRecord {
    accountId?: string;
    facilityId?: string;
    enabled?: boolean;
}

/**
 * A fingerprint of everything synced site entitlements are resolved from, for a reaction to watch cheaply; `feature` is the
 * `SiteScopedPackages` feature definition, whose arrival or default decides the flag when no account sets it.
 */
export function syncedEntitlementInputs(
    records: ReadonlyArray<SyncedProductPackageRecord>,
    sites: ReadonlyArray<SyncedSiteProductPackages>,
    flags: ReadonlyArray<SyncedFlagRecord>,
    feature: { defaultValue?: boolean } | undefined,
): string {
    return JSON.stringify([
        [!!feature, feature?.defaultValue],
        records.map(record => [record.id, record.accountId, record.features, record.packages, record.ownerAccountId]),
        sites.map(site => [site.id, site.packages]),
        flags.map(flag => [flag.accountId, flag.facilityId, flag.enabled]),
    ]);
}

/** What a client reports when none of a site's synced packages is defined; matches `ProductPackageEntitlementFallback`. */
export type SyncedEntitlementFallbackReport = {
    accountId: string;
    facilityId?: string;
    reason: SyncedEntitlementFallback;
    packageIds: string;
};

/** Logs and emits each account and site whose synced packages are all undefined, once until cleared. */
export class SyncedEntitlementFallbackReporter {
    private readonly reported = new Set<string>();

    /** @param emit sends the report as telemetry; called once per account and site. */
    constructor(private readonly emit: (report: SyncedEntitlementFallbackReport) => void) {}

    /** Reports `result` for the site when it fell back because none of its packages is defined. */
    report(accountId: string, facilityId: string | undefined, result: SyncedSiteEntitlements): void {
        if (result.fallback !== SyncedEntitlementFallback.NoDefinedPackages) return;
        const key = `${accountId}:${facilityId ?? ''}`;
        if (this.reported.has(key)) return;
        this.reported.add(key);
        const report = { accountId, facilityId, reason: result.fallback, packageIds: (result.packageIds ?? []).join(',') };
        console.warn('Product packages not used; using the account\'s enabled actions', report);
        this.emit(report);
    }

    /** Forgets what was reported, e.g. when the user or their permissions change. */
    clear(): void {
        this.reported.clear();
    }
}
